/**
 * /estimate — Public trip cost calculator using 1TEH's published rate sheet.
 * Range-based (per the sheet), with breakdown. No login required.
 * Doubles as a marketing conversion tool.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Calculator, MapPin, Users, Clock, Sparkles, ArrowRight, Loader2, ShieldCheck, Mail, Bookmark, LocateFixed,
} from "lucide-react";
import { API } from "@/lib/api";
import { patientApi } from "@/lib/patientApi";

const money = (c) => `$${(c / 100).toFixed(2)}`;

const TRIP_CLASSES = [
  { key: "local_oneway", label: "Local one-way", hint: "Base $40–$70" },
  { key: "roundtrip_under10", label: "Round trip <10 mi", hint: "$80–$150" },
  { key: "dialysis_rt", label: "Dialysis round trip", hint: "$80–$120 / session" },
  { key: "weekly_dialysis_pkg", label: "Weekly dialysis (3×/wk)", hint: "$250–$400" },
  { key: "weekly_medical_pkg", label: "Weekly medical", hint: "$400–$500" },
];

// Real vehicle capacities per mobility type
const MOBILITY = [
  { k: "ambulatory", label: "Ambulatory", capacity: 4, vehicle: "Sedan / SUV" },
  { k: "wheelchair", label: "Wheelchair", capacity: 3, vehicle: "Wheelchair van (1 chair + 2 riders)" },
  { k: "stretcher", label: "Stretcher", capacity: 2, vehicle: "Stretcher van (1 patient + 1 caregiver)" },
  { k: "assist_only", label: "Assist only", capacity: 4, vehicle: "Sedan / SUV" },
];
const MULTI_TRIP_DISCOUNTS = [0, 5, 10, 15, 20]; // percent — dispatcher-negotiable

async function drivingMiles(pickup, dropoff) {
  // pickup / dropoff are [lng, lat] tuples
  if (!pickup || !dropoff) return null;
  // OSRM public API — free, no key, driving distance in meters
  try {
    const url = `https://router.project-osrm.org/route/v1/driving/${pickup[0]},${pickup[1]};${dropoff[0]},${dropoff[1]}?overview=false`;
    const r = await fetch(url);
    const d = await r.json();
    const meters = d?.routes?.[0]?.distance;
    if (typeof meters === "number" && meters > 0) {
      return { miles: Math.round((meters / 1609.344) * 10) / 10, source: "driving" };
    }
  } catch (_) { /* fall through to Haversine */ }
  // Haversine fallback (straight-line)
  const R = 3958.8;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(dropoff[1] - pickup[1]);
  const dLon = toRad(dropoff[0] - pickup[0]);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(pickup[1])) * Math.cos(toRad(dropoff[1])) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return { miles: Math.round(R * c * 10) / 10, source: "straight-line" };
}

// US Census Bureau Geocoder — free, no key, US-only, very accurate for street addresses.
// https://geocoding.geo.census.gov/geocoder/
async function censusSearch(q) {
  const url = `https://geocoding.geo.census.gov/geocoder/locations/onelineaddress`
    + `?address=${encodeURIComponent(q)}`
    + `&benchmark=Public_AR_Current&format=jsonp&callback=cb`;
  // JSONP because Census doesn't send CORS headers
  const res = await fetch(url).then((r) => r.text()).catch(() => null);
  if (!res) return [];
  const m = res.match(/^cb\((.*)\)$/s);
  if (!m) return [];
  try {
    const data = JSON.parse(m[1]);
    const matches = data?.result?.addressMatches || [];
    return matches.map((mm) => ({
      label: mm.matchedAddress,
      lat: mm.coordinates?.y,
      lon: mm.coordinates?.x,
    }));
  } catch {
    return [];
  }
}
async function censusReverse(lat, lon) {
  const url = `https://geocoding.geo.census.gov/geocoder/geographies/coordinates`
    + `?x=${lon}&y=${lat}&benchmark=Public_AR_Current&vintage=Current_Current&format=jsonp&callback=cb`;
  const res = await fetch(url).then((r) => r.text()).catch(() => null);
  if (!res) return null;
  const m = res.match(/^cb\((.*)\)$/s);
  if (!m) return null;
  try {
    const data = JSON.parse(m[1]);
    // Census reverse doesn't return street address — fall back to lat/lon label
    const county = data?.result?.geographies?.["Counties"]?.[0]?.NAME;
    const state = data?.result?.geographies?.["States"]?.[0]?.STUSAB;
    return { label: [county, state].filter(Boolean).join(", ") || null };
  } catch {
    return null;
  }
}

async function searchAddress(q) {
  if (!q || q.length < 5) return [];
  let results = await censusSearch(q);
  if (results.length === 0) {
    try {
      const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=5&lang=en`);
      const d = await r.json();
      results = (d.features || []).map((f) => ({
        label: [f.properties.name, f.properties.street, f.properties.city || f.properties.town,
                f.properties.state, f.properties.postcode].filter(Boolean).join(", "),
        lat: f.geometry?.coordinates?.[1],
        lon: f.geometry?.coordinates?.[0],
      }));
    } catch (_) { /* silent */ }
  }
  return results;
}

function AddressField({ label, value, onChange, onCoords, testid, allowLocation }) {
  const [sugs, setSugs] = useState([]);
  const [open, setOpen] = useState(false);
  const [locBusy, setLocBusy] = useState(false);
  const [searching, setSearching] = useState(false);
  const timer = useRef(null);

  const change = (v) => {
    onChange(v);
    onCoords(null); // any edit invalidates prior geocode
    if (timer.current) clearTimeout(timer.current);
    if (v.length < 5) { setSugs([]); return; }
    // Debounce Census/Photon lookup
    timer.current = setTimeout(async () => {
      setSearching(true);
      const results = await searchAddress(v);
      setSugs(results.slice(0, 6));
      setOpen(true);
      setSearching(false);
    }, 500);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location not available on this device.");
      return;
    }
    setLocBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        // Census reverse only gives county/state — for a real street address we
        // fetch Photon reverse for the label, then let the user tap "confirm" to accept.
        let label = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
        try {
          const r = await fetch(`https://photon.komoot.io/reverse?lat=${latitude}&lon=${longitude}&lang=en`);
          const d = await r.json();
          const p = d.features?.[0]?.properties;
          if (p) {
            label = [p.name, p.street, p.city || p.town, p.state, p.postcode].filter(Boolean).join(", ");
          }
        } catch (_) { /* silent */ }
        onChange(label);
        onCoords([longitude, latitude]);
        // Also load 3 nearby Census matches so the user can pick a more exact address
        try {
          const nearby = await censusSearch(label);
          if (nearby.length > 0) {
            setSugs(nearby);
            setOpen(true);
            toast.info("Pick your exact address below for accuracy.");
          } else {
            toast.success("Using your current location");
          }
        } catch (_) {
          toast.success("Using your current location");
        }
        setLocBusy(false);
      },
      (err) => {
        setLocBusy(false);
        toast.error(err.code === 1 ? "Location permission denied." : "Couldn't get your location.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-brand-deep">{label}</label>
        {allowLocation && (
          <button
            type="button"
            onClick={useMyLocation}
            disabled={locBusy}
            data-testid={`${testid}-locate`}
            className="text-[11px] inline-flex items-center gap-1 text-brand-navy hover:text-brand-green font-medium disabled:opacity-60"
          >
            {locBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LocateFixed className="w-3 h-3" />}
            Use my location
          </button>
        )}
      </div>
      <div className="relative">
        <input
          value={value}
          onChange={(e) => change(e.target.value)}
          onFocus={() => value.length >= 5 && sugs.length > 0 && setOpen(true)}
          onBlur={async () => {
            setTimeout(() => setOpen(false), 200);
            // If user typed an address but never picked a suggestion, auto-lock
            // the top match's coords so mileage still computes.
            if (value.length >= 5) {
              const top = sugs[0];
              if (top && top.lat && top.lon) {
                onCoords([top.lon, top.lat]);
              } else {
                const fresh = await searchAddress(value);
                if (fresh[0]?.lat && fresh[0]?.lon) {
                  onCoords([fresh[0].lon, fresh[0].lat]);
                }
              }
            }
          }}
          placeholder="Street address, city, state (e.g. 100 Auburn Ave NE, Atlanta, GA)"
          data-testid={testid}
          className="w-full px-4 py-3 rounded-lg border border-brand-navy/15 focus:outline-none focus:border-brand-green"
        />
        {searching && (
          <Loader2 className="w-4 h-4 animate-spin text-brand-muted absolute right-3 top-1/2 -translate-y-1/2" />
        )}
      </div>
      {open && sugs.length > 0 && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-brand-navy/15 rounded-lg max-h-56 overflow-auto shadow-lg">
          {sugs.map((s, i) => (
            <button
              key={i}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(s.label);
                if (s.lat && s.lon) onCoords([s.lon, s.lat]);
                setOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-brand-deep hover:bg-brand-surfaceAlt border-b border-brand-navy/5 last:border-b-0"
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Estimate() {
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [tripClass, setTripClass] = useState("local_oneway");
  const [pickup, setPickup] = useState("");
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoff, setDropoff] = useState("");
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [manualMiles, setManualMiles] = useState("");
  const [milesAuto, setMilesAuto] = useState(null); // { miles, source }
  const [milesBusy, setMilesBusy] = useState(false);
  const [mobility, setMobility] = useState("ambulatory");
  const [passengers, setPassengers] = useState(1);
  const [ridingKind, setRidingKind] = useState("all_riding"); // all_riding | one_patient
  const [multiDiscount, setMultiDiscount] = useState(0); // 0/5/10/15/20 %
  const [roundTrip, setRoundTrip] = useState(false);
  const [waitMinutes, setWaitMinutes] = useState(0);
  const [afterHours, setAfterHours] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [emailAddr, setEmailAddr] = useState("");
  const [showEmail, setShowEmail] = useState(false);

  // Post height for embed auto-resize
  useEffect(() => {
    if (!embed) return;
    const post = () => {
      window.parent?.postMessage(
        { source: "teh-estimator", height: document.body.scrollHeight },
        "*"
      );
    };
    post();
    const t = setInterval(post, 1000);
    return () => clearInterval(t);
  }, [embed, result]);

  const canSubmit = useMemo(() => {
    return tripClass && (
      manualMiles.trim() !== "" ||
      (pickupCoords && dropoffCoords) ||
      (pickup.trim() && dropoff.trim()) ||   // typed addresses — we'll geocode on submit
      ["dialysis_rt", "weekly_dialysis_pkg", "weekly_medical_pkg", "roundtrip_under10"].includes(tripClass)
    );
  }, [tripClass, manualMiles, pickup, dropoff, pickupCoords, dropoffCoords]);

  const vehicleInfo = useMemo(
    () => MOBILITY.find((m) => m.k === mobility) || MOBILITY[0],
    [mobility]
  );

  // Auto-calculate driving miles whenever both pickup + dropoff coords are locked in.
  // Populates `manualMiles` so the user sees the number before hitting Calculate.
  useEffect(() => {
    if (!pickupCoords || !dropoffCoords) {
      setMilesAuto(null);
      return;
    }
    let cancelled = false;
    setMilesBusy(true);
    drivingMiles(pickupCoords, dropoffCoords).then((res) => {
      if (cancelled || !res) return;
      setMilesAuto(res);
      setManualMiles(String(res.miles));
      setMilesBusy(false);
    });
    return () => { cancelled = true; setMilesBusy(false); };
  }, [pickupCoords, dropoffCoords]);

  // If the passenger count exceeds vehicle capacity AND the user says "all of them
  // are riding", we need multiple trips. Otherwise the effective on-board passenger
  // count is 1 (only the patient).
  const multiTrip = useMemo(() => {
    const cap = vehicleInfo.capacity;
    if (passengers <= cap) return { trips: 1, perVehiclePax: passengers, needed: false };
    if (ridingKind === "one_patient") {
      return { trips: 1, perVehiclePax: 1, needed: false };
    }
    return {
      trips: Math.ceil(passengers / cap),
      perVehiclePax: cap,
      needed: true,
    };
  }, [passengers, vehicleInfo, ridingKind]);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      // If the user typed addresses but never picked a suggestion (no coords locked),
      // silently geocode + compute driving distance now so the estimate is address-based.
      let pu = pickupCoords, dp = dropoffCoords;
      if (manualMiles.trim() === "" && (!pu || !dp)) {
        if (!pu && pickup.trim()) {
          const r = await searchAddress(pickup);
          if (r[0]?.lat && r[0]?.lon) { pu = [r[0].lon, r[0].lat]; setPickupCoords(pu); }
        }
        if (!dp && dropoff.trim()) {
          const r = await searchAddress(dropoff);
          if (r[0]?.lat && r[0]?.lon) { dp = [r[0].lon, r[0].lat]; setDropoffCoords(dp); }
        }
      }
      const body = {
        trip_class: tripClass,
        mobility,
        passengers: multiTrip.perVehiclePax,
        round_trip: roundTrip,
        wait_minutes: Number(waitMinutes),
        after_hours: afterHours,
      };
      if (manualMiles.trim() !== "") {
        body.miles = Number(manualMiles);
      } else if (pu && dp) {
        body.pickup_lat = pu[1]; body.pickup_lng = pu[0];
        body.dropoff_lat = dp[1]; body.dropoff_lng = dp[0];
      }
      const res = await fetch(`${API}/nemt/estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Estimate failed");
      setResult(data);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Auto-recalc when the user tweaks anything and there's a valid result already
  useEffect(() => {
    if (!result) return;
    const t = setTimeout(submit, 400);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [tripClass, manualMiles, pickupCoords, dropoffCoords, mobility, passengers, ridingKind, roundTrip, waitMinutes, afterHours]);

  // Multi-trip totals with dispatcher-negotiable discount
  const totals = useMemo(() => {
    if (!result) return null;
    const rawLow = result.low_cents * multiTrip.trips;
    const rawHigh = result.high_cents * multiTrip.trips;
    const disc = multiTrip.trips > 1 ? multiDiscount : 0;
    return {
      raw_low: rawLow,
      raw_high: rawHigh,
      low: Math.round(rawLow * (1 - disc / 100)),
      high: Math.round(rawHigh * (1 - disc / 100)),
      disc_pct: disc,
      savings_low: Math.round(rawLow * disc / 100),
      savings_high: Math.round(rawHigh * disc / 100),
    };
  }, [result, multiTrip, multiDiscount]);

  return (
    <div className="min-h-screen bg-brand-bg">
      {!embed && (
        <div className="bg-brand-deep text-white">
          <div className="max-w-4xl mx-auto px-5 md:px-8 py-10">
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold">Trip cost estimator</div>
            <h1 className="font-serif text-3xl sm:text-4xl mt-2">What will my ride cost?</h1>
            <p className="text-sm text-white/85 mt-3 max-w-2xl">
              Based on the 1st Touch Medical Transport published rate sheet. Estimates are a range —
              dispatch confirms the final price at scheduling. Medicaid, Medicare, and contracted broker rates may differ.
            </p>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-8 grid gap-6 md:grid-cols-5">
        <form onSubmit={submit} className="md:col-span-3 space-y-5" data-testid="estimate-form">
          {/* Trip type */}
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold mb-3">Trip type</div>
            <div className="space-y-2">
              {TRIP_CLASSES.map((t) => (
                <label key={t.key} className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border cursor-pointer transition ${tripClass === t.key ? "border-brand-green bg-emerald-50" : "border-brand-navy/15"}`}>
                  <div>
                    <input type="radio" name="trip_class" value={t.key} checked={tripClass === t.key} onChange={() => setTripClass(t.key)} className="hidden" data-testid={`est-class-${t.key}`} />
                    <div className="text-sm font-medium text-brand-deep">{t.label}</div>
                  </div>
                  <div className="text-xs text-brand-muted font-mono">{t.hint}</div>
                </label>
              ))}
            </div>
          </div>

          {/* Addresses (all trip types) */}
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-4">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Distance</div>
            <AddressField label="Pickup" value={pickup} onChange={setPickup} onCoords={setPickupCoords} testid="est-pickup" allowLocation />
            <AddressField label="Destination" value={dropoff} onChange={setDropoff} onCoords={setDropoffCoords} testid="est-dropoff" />
            <div className="text-xs text-brand-muted">— or enter distance directly —</div>
            <div>
              <label className="text-xs font-medium text-brand-deep flex items-center justify-between mb-1">
                <span>Miles (approx.)</span>
                {milesBusy && <Loader2 className="w-3 h-3 animate-spin text-brand-muted" />}
                {!milesBusy && milesAuto && (
                  <span data-testid="est-auto-miles" className="text-[11px] text-emerald-700 font-medium">
                    Auto: {milesAuto.miles} mi ({milesAuto.source})
                  </span>
                )}
              </label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={manualMiles}
                onChange={(e) => setManualMiles(e.target.value)}
                data-testid="est-miles"
                placeholder="e.g. 8"
                className="w-full px-4 py-3 rounded-lg border border-brand-navy/15"
              />
              <div className="text-xs text-brand-muted mt-1">Auto-filled from your addresses. Edit to override.</div>
            </div>
          </div>

          {/* Mobility + passengers */}
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">Vehicle & riders</div>
            <div className="flex flex-wrap gap-2">
              {MOBILITY.map((m) => (
                <label key={m.k} className={`px-3 py-2 rounded-full border text-xs font-medium cursor-pointer transition ${mobility === m.k ? "border-brand-green bg-emerald-50 text-brand-green" : "border-brand-navy/15 text-brand-deep"}`}>
                  <input type="radio" name="mobility" value={m.k} checked={mobility === m.k} onChange={() => setMobility(m.k)} className="hidden" data-testid={`est-mobility-${m.k}`} />
                  {m.label}
                </label>
              ))}
            </div>
            <div className="text-[11px] text-brand-muted" data-testid="est-vehicle-hint">
              {vehicleInfo.vehicle} · seats up to <strong>{vehicleInfo.capacity}</strong> per vehicle.
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <div>
                <label className="text-xs font-medium text-brand-deep flex items-center gap-1.5 mb-1"><Users className="w-3.5 h-3.5" /> Passengers</label>
                <input type="number" min={1} max={99} value={passengers} onChange={(e) => setPassengers(Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                  data-testid="est-passengers" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
                <div className="text-xs text-brand-muted mt-1">Total heads getting in the vehicle</div>
              </div>
              <div>
                <label className="text-xs font-medium text-brand-deep flex items-center gap-1.5 mb-1"><Clock className="w-3.5 h-3.5" /> Wait time (min)</label>
                <input type="number" min={0} max={240} value={waitMinutes} onChange={(e) => setWaitMinutes(Math.max(0, Number(e.target.value) || 0))}
                  data-testid="est-wait" className="w-full px-4 py-2.5 rounded-lg border border-brand-navy/15" />
                <div className="text-xs text-brand-muted mt-1">Fee kicks in at 15+ min</div>
              </div>
            </div>

            {/* Prompt: are all X really riding, or is it 1 patient + observers? */}
            {passengers > vehicleInfo.capacity && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="est-riding-prompt">
                <div className="text-xs font-semibold text-amber-900">
                  {passengers} passengers won&apos;t fit in one {vehicleInfo.vehicle.toLowerCase()} (max {vehicleInfo.capacity}).
                </div>
                <div className="mt-2 space-y-2 text-sm text-brand-deep">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="riding_kind" value="all_riding" checked={ridingKind === "all_riding"} onChange={() => setRidingKind("all_riding")} data-testid="ridekind-all" className="mt-1" />
                    <span>All {passengers} are traveling — we&apos;ll need <strong>{Math.ceil(passengers / vehicleInfo.capacity)} vehicles / trips</strong></span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="radio" name="riding_kind" value="one_patient" checked={ridingKind === "one_patient"} onChange={() => setRidingKind("one_patient")} data-testid="ridekind-one" className="mt-1" />
                    <span>Only 1 patient is being transported — the rest are family/observers, not riding</span>
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Options */}
          <div className="bg-white rounded-2xl border border-brand-navy/10 p-5 space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={roundTrip} onChange={(e) => setRoundTrip(e.target.checked)} data-testid="est-roundtrip" className="w-4 h-4 accent-emerald-600"
                disabled={tripClass !== "local_oneway"} />
              <div className={`text-sm ${tripClass !== "local_oneway" ? "text-brand-muted" : "text-brand-deep"}`}>
                Round-trip (return leg)
                {tripClass !== "local_oneway" && <span className="text-xs ml-2">— already round-trip</span>}
              </div>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={afterHours} onChange={(e) => setAfterHours(e.target.checked)} data-testid="est-afterhours" className="w-4 h-4 accent-emerald-600" />
              <div className="text-sm text-brand-deep">After-hours / weekend (+15%)</div>
            </label>
          </div>

          <button
            type="submit"
            disabled={!canSubmit || busy}
            data-testid="est-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-brand-navy hover:bg-brand-navyDark text-white font-semibold py-4 rounded-full transition disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Calculator className="w-5 h-5" />}
            Calculate estimate
          </button>
        </form>

        {/* Result */}
        <aside className="md:col-span-2 md:sticky md:top-6 self-start" data-testid="estimate-result-panel">
          {!result ? (
            <div className="bg-gradient-to-br from-brand-navy to-brand-deep text-white rounded-2xl p-6">
              <Sparkles className="w-6 h-6 text-emerald-300" />
              <div className="font-serif text-2xl mt-3">Your estimate appears here</div>
              <p className="text-sm text-white/80 mt-2">Fill in the form, then tap <strong>Calculate estimate</strong>.</p>
              <p className="text-xs text-white/70 mt-4">
                Prices come from the 1st Touch Medical Transport rate sheet. Dispatch confirms the final total at scheduling —
                Medicaid, Medicare, and contracted broker rates may differ.
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border-2 border-brand-green p-6" data-testid="estimate-result">
              {multiTrip.needed && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-xl" data-testid="multi-trip-banner">
                  <div className="flex items-center gap-2 text-amber-900 font-semibold text-sm">
                    <Users className="w-4 h-4" /> Multiple trips needed
                  </div>
                  <div className="text-xs text-amber-800 mt-1">
                    {passengers} passengers won&apos;t fit in one {vehicleInfo.vehicle.toLowerCase()} —
                    dispatch will assign <strong>{multiTrip.trips} vehicles / trips</strong> (up to {multiTrip.perVehiclePax} per vehicle),
                    subject to driver availability.
                  </div>
                  <div className="mt-3">
                    <label className="text-[11px] uppercase tracking-[0.14em] text-amber-900 font-semibold">Multi-trip discount</label>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {MULTI_TRIP_DISCOUNTS.map((p) => (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setMultiDiscount(p)}
                          data-testid={`multi-disc-${p}`}
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${
                            multiDiscount === p
                              ? "bg-amber-600 text-white border-amber-600"
                              : "bg-white text-amber-900 border-amber-300 hover:bg-amber-100"
                          }`}
                        >
                          {p === 0 ? "None" : `${p}% off`}
                        </button>
                      ))}
                    </div>
                    <div className="text-[11px] text-amber-800 mt-1.5">Dispatch can negotiate the final discount.</div>
                  </div>
                </div>
              )}

              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-green font-semibold">
                {multiTrip.trips > 1 ? `Estimated total (${multiTrip.trips} trips)` : "Estimated price"}
              </div>
              <div className="font-serif text-4xl text-brand-deep mt-2" data-testid="estimate-range">
                {totals ? `${money(totals.low)} – ${money(totals.high)}` : `${money(result.low_cents)} – ${money(result.high_cents)}`}
              </div>
              {multiTrip.trips > 1 && (
                <div className="text-xs text-brand-muted mt-1" data-testid="estimate-per-trip">
                  Per trip: {money(result.low_cents)} – {money(result.high_cents)}
                  {totals && totals.disc_pct > 0 && (
                    <span className="ml-2 text-emerald-700 font-medium">
                      · {totals.disc_pct}% off saves {money(totals.savings_low)}–{money(totals.savings_high)}
                    </span>
                  )}
                </div>
              )}
              {result.miles && (
                <div className="text-xs text-brand-muted mt-1">{result.miles} miles (straight-line)</div>
              )}

              <div className="mt-5 border-t border-brand-navy/10 pt-4 space-y-2">
                {result.breakdown.map((b, i) => (
                  <div key={i} className="flex items-center justify-between text-sm" data-testid={`estimate-line-${i}`}>
                    <span className="text-brand-deep">{b.label}</span>
                    <span className="font-mono text-brand-muted">{money(b.low_cents)}–{money(b.high_cents)}</span>
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-brand-navy/10 pt-4 flex items-start gap-2 text-xs text-brand-muted">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>{result.note}</span>
              </div>

              {multiTrip.needed && (
                <button
                  onClick={() => setShowEmail(true)}
                  data-testid="request-invoice"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold py-2.5 rounded-full transition"
                >
                  <Mail className="w-4 h-4" /> Request written quote / invoice
                </button>
              )}

              {/* Save + Email actions */}
              <div className="mt-5 grid grid-cols-2 gap-2">
                <button
                  onClick={async () => {
                    setSaveBusy(true);
                    try {
                      await patientApi.saveEstimate({
                        trip_class: tripClass, miles: result.miles,
                        mobility, passengers, round_trip: roundTrip,
                        wait_minutes: waitMinutes, after_hours: afterHours,
                        pickup: pickup || null, dropoff: dropoff || null,
                        low_cents: totals ? totals.low : result.low_cents,
                        high_cents: totals ? totals.high : result.high_cents,
                      });
                      toast.success("Saved to your account. Open the patient portal to book it in one tap.");
                    } catch (err) {
                      if (err.status === 401) {
                        toast.error("Sign in to save estimates.");
                        window.location.href = "/patient";
                      } else {
                        toast.error(err.message);
                      }
                    } finally { setSaveBusy(false); }
                  }}
                  disabled={saveBusy}
                  data-testid="estimate-save"
                  className="inline-flex items-center justify-center gap-1.5 border border-brand-navy/20 hover:border-brand-navy text-brand-deep text-xs font-medium py-2.5 rounded-full transition disabled:opacity-60"
                >
                  {saveBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bookmark className="w-3.5 h-3.5" />}
                  Save
                </button>
                <button
                  onClick={() => setShowEmail((v) => !v)}
                  data-testid="estimate-email-toggle"
                  className="inline-flex items-center justify-center gap-1.5 border border-brand-navy/20 hover:border-brand-navy text-brand-deep text-xs font-medium py-2.5 rounded-full transition"
                >
                  <Mail className="w-3.5 h-3.5" /> Email me
                </button>
              </div>

              {showEmail && (
                <div className="mt-3 flex items-center gap-2" data-testid="estimate-email-panel">
                  <input
                    type="email"
                    placeholder="Your email"
                    value={emailAddr}
                    onChange={(e) => setEmailAddr(e.target.value)}
                    data-testid="estimate-email-input"
                    className="flex-1 px-3 py-2 rounded-lg border border-brand-navy/15 text-sm"
                  />
                  <button
                    onClick={async () => {
                      if (!emailAddr) return;
                      setEmailBusy(true);
                      try {
                        const tripCount = multiTrip.trips;
                        const summary = tripCount > 1
                          ? `${tripCount} trips · ${TRIP_CLASSES.find(t => t.key === tripClass)?.label} · ${mobility}${result.miles ? ` · ${result.miles} mi each` : ""}`
                          : `${TRIP_CLASSES.find(t => t.key === tripClass)?.label} · ${mobility}${result.miles ? ` · ${result.miles} mi` : ""}`;
                        const details = result.breakdown.map((b) =>
                          `<tr><td style="padding:4px 8px;">${b.label}</td><td style="padding:4px 8px;text-align:right;font-family:monospace;">$${(b.low_cents / 100).toFixed(2)}&ndash;$${(b.high_cents / 100).toFixed(2)}</td></tr>`
                        ).join("");
                        let details_html = `<table style="border-collapse:collapse;width:100%;font-size:13px;margin:12px 0;"><tbody>${details}</tbody></table>`;
                        if (tripCount > 1) {
                          details_html += `<p style="margin-top:12px;font-size:13px;"><strong>${tripCount} vehicles / trips needed</strong> (${passengers} passengers, max ${vehicleInfo.capacity} per ${vehicleInfo.vehicle.toLowerCase()})`;
                          if (totals && totals.disc_pct > 0) {
                            details_html += ` · <span style="color:#0E7A3D;">${totals.disc_pct}% multi-trip discount applied</span>`;
                          }
                          details_html += `</p>`;
                        }
                        details_html += `<p style="font-size:12px;color:#666;margin-top:8px;">Reply to this email to have dispatch confirm the exact quote and send an invoice.</p>`;
                        const sendLow = totals ? totals.low : result.low_cents;
                        const sendHigh = totals ? totals.high : result.high_cents;
                        const res = await fetch(`${API}/nemt/estimate/email`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            email: emailAddr, summary,
                            low_cents: sendLow, high_cents: sendHigh,
                            details_html,
                          }),
                        });
                        if (!res.ok) throw new Error("Send failed");
                        toast.success("Sent! Check your inbox.");
                        setShowEmail(false);
                        setEmailAddr("");
                      } catch (err) {
                        toast.error(err.message);
                      } finally { setEmailBusy(false); }
                    }}
                    disabled={emailBusy || !emailAddr}
                    data-testid="estimate-email-send"
                    className="px-4 py-2 rounded-full bg-brand-navy hover:bg-brand-navyDark text-white text-xs font-medium disabled:opacity-60"
                  >
                    {emailBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Send"}
                  </button>
                </div>
              )}

              <a
                href="/patient"
                data-testid="estimate-book-cta"
                className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-3 rounded-full transition"
              >
                Book this ride <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
